<template>
  <ResourceDialog
    @close="emit('close')"
    ref="dialog"
    :max-width="600"
    :processing="processing"
    :title="task.name"
    cancel-button-text="Close"
    closeable
    hide-save-button
  >
    <template #form>
      <div class="d-flex flex-column ga-3">
        <v-row
          v-if="task.description"
          dense
          no-gutters
        >
          <LabeledControl
            cols="12"
            message="Description"
          >
            <p>
              {{ task.description }}
            </p>
          </LabeledControl>
        </v-row>

        <v-row
          dense
          no-gutters
        >
          <LabeledControl
            cols="6"
            message="Workflow"
          >
            <router-link
              v-if="workflowRoute"
              :to="workflowRoute"
              class="text-primary"
            >
              {{ task.workflow_name || 'N/A' }}
            </router-link>

            <span v-else>{{ task.workflow_name || 'N/A' }}</span>
          </LabeledControl>

          <LabeledControl
            cols="6"
            message="Status"
          >
            <StandardChip
              :text="task.status"
              :text-translate="false"
              role="status"
              capitalize
            />
          </LabeledControl>
        </v-row>

        <v-row
          dense
          no-gutters
        >
          <LabeledControl
            cols="6"
            message="Due date"
          >
            <LongDate
              v-if="task.due_at"
              :date="task.due_at"
              data-testid="due-date"
              date-classes="fs-16"
            />

            <span v-else>{{ blankValue }}</span>
          </LabeledControl>
        </v-row>

        <v-row
          v-if="formStatus"
          dense
          no-gutters
        >
          <LabeledControl
            cols="12"
            message="Form status"
          >
            <StandardChip
              :text="formStatus"
              :text-translate="false"
              data-testid="form-status-chip"
              role="status"
            />
          </LabeledControl>
        </v-row>

        <v-row
          v-if="assessmentStatus"
          dense
          no-gutters
        >
          <LabeledControl
            cols="12"
            message="Assessment status"
          >
            <StandardChip
              :text="assessmentStatus"
              :text-translate="false"
              data-testid="assessment-status-chip"
              role="status"
            />
          </LabeledControl>
        </v-row>

        <v-row
          v-if="showAgreementSection"
          dense
          no-gutters
        >
          <LabeledControl
            cols="12"
            message="Agreement"
          >
            <button
              v-if="agreementArtifact?.artifact_id"
              @click="openAgreement"
              class="text-primary underlined pointer bg-transparent border-0 pa-0"
              data-testid="agreement-status-link"
              type="button"
            >
              <StandardChip
                :text="agreementStatus"
                :text-translate="false"
                data-testid="agreement-status-chip"
                role="status"
              />
            </button>

            <span
              v-else
              class="text-medium-emphasis"
              data-testid="agreement-not-created"
            >
              Not created yet
            </span>
          </LabeledControl>
        </v-row>

        <v-row
          v-if="task.status === 'completed'"
          dense
          no-gutters
        >
          <LabeledControl
            cols="12"
            message="Completed"
          >
            <LongDate
              v-if="task.completed_at"
              :date="task.completed_at"
              date-classes="fs-16"
            />

            <span v-else>{{ blankValue }}</span>
          </LabeledControl>
        </v-row>
      </div>
    </template>

    <template #actions>
      <StandardButton
        @click="emit('close')"
        :custom-classes="actionButtonClasses('me-3')"
        :full-width="$vuetify.display.smAndDown"
        :loading="processing"
        color="primary"
        text="Close"
        variant="outlined"
      />

      <div
        v-if="rightActionButtons.primary"
        :class="$vuetify.display.mdAndUp ? 'flex-row d-flex' : 'w-100'"
      >
        <StandardButton
          v-if="rightActionButtons.secondary"
          @click="rightActionButtons.secondary.onClick"
          :custom-classes="actionButtonClasses('me-3')"
          :disabled-text="rightActionButtons.secondary.disabledText"
          :full-width="$vuetify.display.smAndDown"
          :loading="processing"
          :text="rightActionButtons.secondary.text"
          role="secondary"
        />

        <StandardButton
          @click="rightActionButtons.primary.onClick"
          :custom-classes="actionButtonClasses()"
          :data-testid="rightActionButtons.primary.dataTestid"
          :disabled-text="rightActionButtons.primary.disabledText"
          :full-width="$vuetify.display.smAndDown"
          :loading="processing"
          :text="rightActionButtons.primary.text"
        />
      </div>
    </template>
  </ResourceDialog>

  <AgreementDialog
    @change="handleAgreementChange"
    ref="agreementDialog"
    :role="agreementDialogRole"
  />

  <WorkflowTaskAgreementDialog
    v-if="agreementCreateDialogProps"
    @created="handleAgreementCreated"
    ref="agreementCreateDialog"
    :group-id="agreementCreateDialogProps.groupId"
    :license-id="licenseId"
    :provider-id="agreementCreateDialogProps.providerId"
    :schema-id="agreementCreateDialogProps.schemaId"
    :workflow-task-id="agreementCreateDialogProps.workflowTaskId"
  />
</template>

<script setup>
import { useDisplay } from 'vuetify';
import { useRouter } from 'vue-router';
import { useStore } from 'vuex';

import AgreementDialog from '@/shared/components/agreement/AgreementDialog.vue';
import { isNextAgreementSigner } from '@/shared/services/agreement-signing';
import LabeledControl from '@/shared/components/form/LabeledControl.vue';
import LongDate from '@/shared/components/LongDate.vue';
import ResourceDialog from '@/shared/components/form/ResourceDialog.vue';
import useApi from '@/shared/composables/useApi';
import useRole from '@/shared/composables/useRole';
import useRolePermissions from '@/shared/composables/useRolePermissions';
import WorkflowTaskAgreementDialog from '@/shared/components/workflows/WorkflowTaskAgreementDialog.vue';

const { api: Api, scopedApi } = useApi();
const router = useRouter();
const store = useStore();
const { isManager, isSpecialist } = useRole();
const { caseSupervisor } = useRolePermissions(store);
const { smAndDown } = useDisplay();

const blankValue = ref('--');
const dialog = ref(null);
const agreementDialog = ref(null);
const agreementCreateDialog = ref(null);
const processing = ref(false);

const props = defineProps({
  licenseId: {
    type: String,
    default: null,
  },
  open: {
    type: Boolean,
    default: false,
  },
  task: {
    type: Object,
    required: true,
  },
});

const emit = defineEmits(['close', 'updated']);

const canMarkComplete = computed(() => {
  if (props.task.status !== 'open') return false;
  if (expectsAgreementArtifact.value) return false;

  if (props.licenseId) {
    return isSpecialist && Boolean(props.task.can_complete);
  }

  return isManager;
});

const formArtifact = computed(() => {
  const artifact = props.task.workflow_artifact;
  return artifact?.artifact_type === 'Form' ? artifact : null;
});

const assessmentArtifact = computed(() => {
  const artifact = props.task.workflow_artifact;
  return artifact?.artifact_type === 'Assessment' ? artifact : null;
});

const agreementArtifactTemplate = computed(() => {
  const template = props.task.workflow_artifact_template;
  return template?.artifact_type === 'Agreement' ? template : null;
});

const agreementArtifact = computed(() => {
  const artifact = props.task.workflow_artifact;
  return artifact?.artifact_type === 'Agreement' ? artifact : null;
});

const agreementCreateDialogProps = computed(() => {
  if (!agreementArtifactTemplate.value || agreementArtifact.value?.artifact_id) return null;
  if (!agreementArtifactTemplate.value.source_schema_id) return null;

  return {
    groupId: resolvedGroupId.value,
    providerId: resolvedProviderId.value,
    schemaId: agreementArtifactTemplate.value.source_schema_id,
    workflowTaskId: props.task.id,
  };
});

const agreementDialogRole = computed(() => (isSpecialist ? 'organization' : 'manager'));

const agreementSummary = computed(() => agreementArtifact.value?.agreement ?? null);

const canSignAgreement = computed(() =>
  isNextAgreementSigner(agreementSummary.value, store.state.profile?.id),
);

const expectsAgreementArtifact = computed(
  () => Boolean(agreementArtifactTemplate.value) && !agreementArtifact.value?.artifact_id,
);

const resolvedProviderId = computed(() => props.task.provider_id ?? props.task.providerId ?? null);

const resolvedGroupId = computed(() => props.task.group_id ?? props.task.groupId ?? null);

const showAgreementSection = computed(() =>
  Boolean(agreementArtifactTemplate.value || agreementArtifact.value),
);

const workflowRoute = computed(() => {
  if (!props.task.workflow_instance_id) return null;

  if (props.licenseId) {
    return {
      name: 'LicenseWorkflowInstanceShow',
      params: {
        licenseId: props.licenseId,
        workflowInstanceId: props.task.workflow_instance_id,
      },
    };
  }

  return {
    name: 'WorkflowInstanceShow',
    params: { workflowInstanceId: props.task.workflow_instance_id },
  };
});

const formRoute = computed(() => {
  if (!formArtifact.value) return null;

  if (props.licenseId) {
    return {
      name: 'FormShow',
      params: { id: formArtifact.value.artifact_id },
    };
  }

  return {
    name: 'WorkflowTaskFormShow',
    params: { workflowTaskId: props.task.id },
    query: { returnToWorkflowInstanceId: props.task.workflow_instance_id },
  };
});

const assessmentRoute = computed(() => {
  if (!assessmentArtifact.value) return null;

  if (isSpecialist) {
    return {
      name: 'AssessmentShow',
      params: { id: assessmentArtifact.value.artifact_id },
    };
  }

  return {
    name: 'AssessmentShow',
    params: { assessmentId: assessmentArtifact.value.artifact_id },
  };
});

const artifactRoute = computed(() => formRoute.value ?? assessmentRoute.value ?? null);

const openArtifactLabel = computed(() => {
  if (needsAgreementCreation.value) return 'Create agreement';
  if (formArtifact.value) return 'Open form';
  if (assessmentArtifact.value) return 'Open assessment';
  if (canSignAgreement.value) return 'Review and sign';
  if (agreementArtifact.value?.artifact_id) return 'View agreement';
  return null;
});

const needsAgreementCreation = computed(
  () =>
    isSpecialist &&
    props.task.status === 'open' &&
    expectsAgreementArtifact.value &&
    Boolean(agreementCreateDialogProps.value),
);

const formStatus = computed(() => formArtifact.value?.form?.status ?? null);

const assessmentStatus = computed(() => assessmentArtifact.value?.assessment?.status ?? null);

const agreementStatus = computed(() => agreementArtifact.value?.agreement?.status ?? null);

const rightActionButtons = computed(() => {
  const canSkip = props.licenseId && isSpecialist && caseSupervisor && Boolean(props.task.can_skip);
  const canReopen = props.licenseId && isSpecialist && Boolean(props.task.can_reopen);
  const showOpenArtifactOrMarkComplete = Boolean(openArtifactLabel.value || canMarkComplete.value);

  function toAction({ text, loadingText = null, onClick, dataTestid = undefined }) {
    return {
      text,
      dataTestid,
      disabledText: processing.value ? loadingText : null,
      onClick,
    };
  }

  let transition = null;
  if (canSkip) {
    transition = toAction({
      text: 'Skip task',
      loadingText: 'Skipping task...',
      onClick: handleSkip,
    });
  } else if (canReopen) {
    transition = toAction({
      text: 'Reopen task',
      loadingText: 'Reopening task...',
      onClick: handleReopen,
    });
  }

  let openArtifactOrMarkComplete = null;
  if (showOpenArtifactOrMarkComplete) {
    openArtifactOrMarkComplete = toAction({
      text: openArtifactLabel.value ?? 'Mark complete',
      onClick: handleSave,
      dataTestid: 'save-button',
    });
  }

  if (openArtifactOrMarkComplete && transition) {
    return { primary: openArtifactOrMarkComplete, secondary: transition };
  }

  if (openArtifactOrMarkComplete) {
    return { primary: openArtifactOrMarkComplete, secondary: null };
  }

  if (transition) {
    return { primary: transition, secondary: null };
  }

  return { primary: null, secondary: null };
});

function actionButtonClasses(marginClass = '') {
  return [
    smAndDown.value ? 'btn-small' : 'btn-large',
    smAndDown.value ? 'mb-4 ms-0 me-0' : marginClass,
    'focus-very-visible',
    'd-flex',
  ];
}

async function handleSave() {
  if (needsAgreementCreation.value) {
    await agreementCreateDialog.value?.open();
    return;
  }

  if (agreementArtifact.value?.artifact_id) {
    await openAgreement();
    return;
  }

  if (artifactRoute.value) {
    await router.push(artifactRoute.value);
    return;
  }

  await markComplete();
}

function handleAgreementChange() {
  emit('updated');
}

function handleAgreementCreated() {
  emit('updated');
}

async function openAgreement() {
  if (!agreementArtifact.value?.artifact_id) return;

  await agreementDialog.value?.open(agreementArtifact.value.artifact_id);
}

async function handleReopen() {
  await runTaskTransition((id) => scopedApi.workflowTask.reopen(id));
}

async function handleSkip() {
  await runTaskTransition((id) => scopedApi.workflowTask.skip(id));
}

async function load() {
  await nextTick();
  if (!dialog.value) return;

  if (props.open) {
    dialog.value.open();
    return;
  }

  await dialog.value.close();
}

async function markComplete() {
  if (!canMarkComplete.value) return;

  if (props.licenseId) {
    await runTaskTransition((id) => scopedApi.workflowTask.complete(id));
    return;
  }

  processing.value = true;

  try {
    const response = await Api.manager.workflowTask.update(props.task.id, { status: 'completed' });
    if (!response?.data) return;

    emit('updated', response.data);
  } finally {
    processing.value = false;
  }
}

async function runTaskTransition(request) {
  if (processing.value || !scopedApi?.workflowTask) return;

  processing.value = true;

  try {
    const response = await request(props.task.id);
    if (!response?.data) return;

    emit('updated', response.data);
  } finally {
    processing.value = false;
  }
}

watch(() => props.open, load);

onMounted(load);
</script>
